'use client';

import { createContext, useContext, type ReactNode } from 'react';

/**
 * Client-safe configuration values.
 * These are injected from the server at request time via the ConfigProvider.
 *
 * IMPORTANT: Only add values here that are safe to expose to the browser.
 * Sensitive values like API tokens should NEVER be included.
 */
export type ClientConfig = {
  // URLs for client-side navigation and asset loading
  APP_URL: string;
  CDN_URL: string;
  EMBED_URL: string;
  RESIZE_URL: string;
  COOKIE_HANDLER_URL: string;
  GRAPHQL_URL: string;

  // Sanity CMS (public config - no tokens)
  SANITY_PROJECT_ID: string;
  SANITY_DATASET: string;
  SANITY_API_VERSION: string;

  // Environment info
  ENV: string;
  APP_ENV: string;
  IS_STAGING: string;
  CI_COMMIT_SHORT_SHA: string;
  BRANCH_NAME: string;

  // Analytics & Monitoring (client tokens only - safe to expose)
  SEGMENT_API_KEY: string;
  GTMID: string;
  ONETRUST_DATA_DOMAIN_SCRIPT: string;
  STATSIG_CLIENT_TOKEN: string;
  STATSIG_ENVIRONMENT: string;

  // Coralogix RUM — public_key is the browser-side ingest credential, safe to embed in page JS
  CORALOGIX_APPLICATION_ID: string;
  CORALOGIX_PUBLIC_KEY: string;
  CORALOGIX_DOMAIN: string;
  CORALOGIX_ENVIRONMENT: string;

  // Computed values
  WEBSITE_DOMAIN: string;
  USE_CDN: boolean;

  // Feature flags
  BGVIDEO_DEFER: boolean;
  IS_PRODUCTION: boolean;

  // Google auth id
  AUTH_GOOGLE_CLIENT_ID: string;
};

const ClientConfigContext = createContext<ClientConfig | null>(null);

type ClientConfigProviderProps = {
  config: ClientConfig;
  children: ReactNode;
};

/**
 * Provides client-safe configuration values to client components.
 *
 * This provider is rendered in the root layout with values read from
 * environment variables on the server at request time.
 *
 * Usage in client components:
 * ```tsx
 * 'use client';
 * import { useClientConfig } from '@/providers/ClientConfigProvider';
 *
 * function MyComponent() {
 *   const config = useClientConfig();
 *   return <img src={`${config.CDN_URL}/my-image.png`} />;
 * }
 * ```
 */
export function ClientConfigProvider({ config, children }: ClientConfigProviderProps) {
  return <ClientConfigContext.Provider value={config}>{children}</ClientConfigContext.Provider>;
}

/**
 * Hook to access client-safe configuration values.
 *
 * Must be used within a ClientConfigProvider (typically set up in root layout).
 *
 * @throws Error if used outside of ClientConfigProvider
 *
 * @example
 * ```tsx
 * 'use client';
 * import { useClientConfig } from '@/providers/ClientConfigProvider';
 *
 * function MyComponent() {
 *   const config = useClientConfig();
 *   return <a href={config.APP_URL}>Go to App</a>;
 * }
 * ```
 */
export function useClientConfig(): ClientConfig {
  const context = useContext(ClientConfigContext);

  if (!context) {
    throw new Error(
      'useClientConfig must be used within a ClientConfigProvider. ' +
        'Make sure your component is rendered within the root layout.',
    );
  }

  return context;
}
